import React, { useCallback, useEffect, useRef, useState } from "react";
import classNames from "classnames";
import { useWindowSize } from "react-use";
import { Mixpanel } from "lib/mixpanel/mixpanel";

import useSiftedMediaQueries from "lib/hooks/useSiftedMediaQueries";
import useMutationObservable from "content-ui/briefings/hooks/useMutationObservable";
import { NavIcon } from "../../icons/icons";
import { MIXPANEL_TOKEN } from "lib/constants";
import { useUserData } from "lib/store/user";

// eslint-disable-next-line css-modules/no-unused-class
import styles from "./contentNavigation.module.css";
import type { BriefingReportPageData } from "content-ui/briefings/fetchers/briefingReportFetcher";

type NavStates = "scrollingUp" | "scrollingDown" | "top";

type Section = { id: string; navTitle: string; icon?: string };

type NavPositions = {
  [key: string]: { x: number; width: number };
};

export default function ContentNavigation({
  sections,
  activeId,
  briefing,
}: {
  sections: Section[];
  activeId: string;
  briefing: BriefingReportPageData;
}) {
  const mixpanelRef = useRef<Mixpanel | null>(null);
  const { userId } = useUserData();

  useEffect(() => {
    mixpanelRef.current = new Mixpanel(MIXPANEL_TOKEN);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { mobile, desktop, tablet } = useSiftedMediaQueries();
  const { width } = useWindowSize();

  const SCROLL_THRESHOLD = 0;
  const NAV_X_OFFSET = mobile ? 0 : 14;
  const INITIAL_Y_OFFSET = 60;

  const [navState, setNavState] = useState<NavStates>("top");
  const [navBarHeight, setNavBarHeight] = useState<number>(INITIAL_Y_OFFSET);
  const [clicked, setClicked] = useState<string | boolean>(false);

  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const navPositionsRef = useRef<NavPositions | {}>({});

  const filteredSections = sections.filter((section) => {
    if (
      section.id === "deals" &&
      (!briefing.airtableEmbedCode || !briefing.dealsHeading)
    ) {
      return false;
    }

    return section.id !== "feedback";
  });

  // NAVIGATION BAR Y POSITIONING
  useEffect(() => {
    if (desktop) return;

    let prevScrollpos = window.pageYOffset;

    function onScroll() {
      requestAnimationFrame(function tick() {
        const currentScrollPos = window.pageYOffset;
        const scrollingUp = prevScrollpos > currentScrollPos;
        const reachedTop = currentScrollPos <= SCROLL_THRESHOLD;

        if (scrollingUp) {
          setNavState("scrollingUp");
        } else {
          setNavState("scrollingDown");
        }

        if (reachedTop) {
          setNavState("top");
        }

        prevScrollpos = currentScrollPos;
      });
    }

    window.addEventListener("scroll", onScroll);

    return function cleanup() {
      window.removeEventListener("scroll", onScroll);
    };
  }, [desktop]);

  useEffect(() => {
    if (desktop) return;

    const navId = activeId.replace("content__", "");
    if (clicked && clicked !== navId) return;

    const navXPos = navPositionsRef.current?.[navId]?.x;
    setClicked(false);
    scrollContainerRef?.current?.scrollTo({
      left: navXPos,
      behavior: "smooth",
    });
  }, [activeId, clicked, desktop, width]);

  // NAVIGATION BAR HEIGHT
  const onNavMutation = useCallback(
    (mutationList) => {
      const elem = mutationList[0].target;
      const height = window
        .getComputedStyle(elem, null)
        .getPropertyValue("height");

      if (height !== "auto") {
        const heightInt = parseInt(height);
        setNavBarHeight(
          heightInt < INITIAL_Y_OFFSET ? INITIAL_Y_OFFSET : heightInt
        );
      }
    },
    [setNavBarHeight, INITIAL_Y_OFFSET]
  );

  useEffect(() => {
    if (tablet || desktop) {
      if (window.pageYOffset > SCROLL_THRESHOLD) {
        setNavBarHeight(60);
      } else {
        setNavBarHeight(100);
      }
    } else if (mobile) {
      setNavBarHeight(60);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tablet, mobile, desktop, window.pageYOffset]);

  let primaryNav;
  if (typeof window !== "undefined") {
    primaryNav = window.document.querySelector(`#navbar`);
  }
  useMutationObservable(primaryNav, onNavMutation);

  // NAVIGATION BUTTONS X POSITIONING (for mobile and tablet)
  const buttonPositions = () => {
    if (desktop) return {};

    const map = {};
    const getElementPositionById = (id: string) => {
      return (
        document.querySelector(`#nav__${id}`)?.getBoundingClientRect() || {
          x: 0,
          width: 0,
        }
      );
    };

    // This re-calibrates nav positions on window resize
    const firstSectionId = filteredSections[0].id;
    const navOffset = getElementPositionById(firstSectionId).x - NAV_X_OFFSET;

    filteredSections.forEach((section) => {
      const elementPosition = getElementPositionById(section.id);
      map[section.id] = {
        x: elementPosition.x - NAV_X_OFFSET - navOffset,
        width: elementPosition.width,
      };
    });
    return map as NavPositions;
  };

  useEffect(() => {
    navPositionsRef.current = buttonPositions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [width]);

  // TODO: This may prove problematic when we come to lazyload content
  const scrollIntoViewWithOffset = (selector: string) => {
    const newYPosition =
      document.querySelector(selector)?.getBoundingClientRect().top || 0;
    const currentYPosition = document.body.getBoundingClientRect().top;

    // Offset new Y position by nav height if we are scrolling up
    const offset = newYPosition && newYPosition > 0 ? 0 : INITIAL_Y_OFFSET;
    // Offset to overcome additional height from secondary horizontal nav
    const deviceOffset = desktop ? 0 : 60;

    window.scrollTo({
      behavior: "smooth",
      top: newYPosition - currentYPosition - (offset + deviceOffset),
    });
  };

  const outerContainerClasses = classNames(
    `${styles.navigation} fixed flex w-100 flex-row flex-nowrap z-[15] overflow-x-auto overflow-y-hidden`,
    "bg-[#FDFDFB] py-2 pl-4 transition-[top] duration-200 ease-in-out lg:relative lg:top-0",
    "lg:block lg:h-100 lg:flex-col lg:rounded-[2px] lg:border-0 lg:py-3 lg:pl-3"
  );

  return (
    <div
      data-testid={"navigation-scroll-container"}
      className={outerContainerClasses}
      ref={scrollContainerRef}
      style={{
        top:
          (navBarHeight && navState === "scrollingDown") || desktop
            ? "0px"
            : `${navBarHeight}px`,
      }}
    >
      <ul className="flex lg:sticky lg:flex-col">
        {filteredSections.map((section, i) => {
          const isActive = `content__${section.id}` === activeId;
          const hasIcon = section.id !== "intro";

          return (
            <li
              id={`nav__${section.id}`}
              key={`nav__${section.id}`}
              className={classNames("heading__nav", { active: isActive })}
            >
              <button
                className={classNames(
                  "mr-4 inline-block cursor-pointer select-none whitespace-nowrap",
                  "rounded-md px-[14px] py-[5px] text-[16px] text-[#002EA5]",
                  {
                    "bg-[#002EA5] fill-white font-[700] text-[#fff]": isActive,
                    "lg:mt-1": i !== 0,
                  }
                )}
                onClick={(e) => {
                  e.preventDefault();

                  if (userId && mixpanelRef.current) {
                    mixpanelRef.current.track("Briefing Navigation", {
                      "Section Clicked": section.id,
                      distinct_id: userId,
                      "Briefing title": briefing.name,
                    });
                  }

                  setClicked(section.id);
                  scrollIntoViewWithOffset(`#content__${section.id}`);
                }}
              >
                <div className={`${hasIcon && "lg:-ml-1"}`}>
                  {hasIcon && (
                    <span className="mr-2 inline-block">
                      <NavIcon
                        icon={section.id}
                        colour={isActive ? "white" : "#002EA5"}
                      />
                    </span>
                  )}
                  {section.navTitle}
                </div>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
